/**
 * Coupon entry, apply, and remove. This component never decides whether a
 * coupon is valid — it only sends the typed code to `onApply` and renders
 * whatever the backend Pricing Engine ultimately decided (reflected back to
 * it via `appliedCouponCode`/`couponError` from `usePricing`). Removing a
 * coupon always triggers a fresh, authoritative recalculation via `onRemove`
 * — it never just subtracts the previous discount locally (Pricing Engine
 * architecture §9 "Remove coupon").
 */
import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Icons } from '@/src/modules/components/Icons';
import { Button } from '@/src/modules/components/Button';
import { TextField } from '@/src/modules/components/settings/primitives';

export interface CouponInputProps {
  appliedCouponCode: string | null;
  couponError?: string | null;
  /** True while a pricing calculation (apply, remove, or anything else) is in flight — disables input to prevent a double-submit race. */
  busy?: boolean;
  onApply: (code: string) => void | Promise<void>;
  onRemove: () => void | Promise<void>;
  /** Notifies parent of un-applied draft text changes; reset to '' when a coupon is applied or on unmount. */
  onDraftChange?: (draft: string) => void;
}

export function CouponInput({ appliedCouponCode, couponError, busy, onApply, onRemove, onDraftChange }: CouponInputProps) {
  const [code, setCode] = useState('');
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;

  useEffect(() => {
    if (appliedCouponCode) {
      setCode('');
      onDraftChangeRef.current?.('');
    }
  }, [appliedCouponCode]);

  useEffect(() => {
    return () => {
      onDraftChangeRef.current?.('');
    };
  }, []);

  const handleApply = () => {
    const trimmed = code.trim();
    if (!trimmed || busy) return;
    void onApply(trimmed);
  };

  if (appliedCouponCode) {
    return (
      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold text-on-surface-variant" id="coupon-applied-label">
          Coupon
        </span>
        <div
          aria-labelledby="coupon-applied-label"
          className="flex items-center justify-between gap-3 rounded-xl border border-status-published/30 bg-status-published/5 px-3.5 py-2.5"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-status-published">
            <Icons.CheckCircle size={16} />
            {appliedCouponCode} applied
          </span>
          <button
            type="button"
            onClick={() => void onRemove()}
            disabled={busy}
            aria-label={`Remove coupon ${appliedCouponCode}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-on-surface-variant hover:text-status-failed transition-colors disabled:opacity-50"
          >
            <X size={14} />
            Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-end gap-2">
        <TextField
          label="Coupon code"
          id="coupon-code-input"
          value={code}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const next = e.target.value.toUpperCase();
            setCode(next);
            onDraftChange?.(next);
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleApply();
            }
          }}
          placeholder="Enter coupon code"
          disabled={busy}
          error={couponError ?? undefined}
          maxLength={40}
          className="flex-1"
        />
        <Button
          type="button"
          variant="outline"
          size="md"
          onClick={handleApply}
          disabled={busy || !code.trim()}
          loading={busy}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}
